import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, Unlock, Loader2, RefreshCw, CalendarDays, User, Phone, MessageSquare, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingPanel = ({ token }) => {
  const [data, setData] = useState({ days: [] });
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingIso, setPendingIso] = useState(null);

  const headers = { 'X-Admin-Token': token };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [slotsRes, bookRes] = await Promise.all([
        axios.get(`${API}/booking/slots`),
        axios.get(`${API}/admin/bookings`, { headers }),
      ]);
      setData(slotsRes.data);
      setLeads(bookRes.data?.bookings || []);
    } catch {/* silent */}
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const blockSlot = async (slot_iso) => {
    setPendingIso(slot_iso);
    try {
      await axios.post(`${API}/admin/slot-block`, { slot_iso, reason: 'Bloccato da admin' }, { headers });
      toast.success('Slot bloccato — non visibile al pubblico');
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel blocco');
    } finally {
      setPendingIso(null);
    }
  };

  const unblockSlot = async (slot_iso) => {
    setPendingIso(slot_iso);
    try {
      await axios.delete(`${API}/admin/slot-block?slot_iso=${encodeURIComponent(slot_iso)}`, { headers });
      toast.success('Slot sbloccato — di nuovo prenotabile');
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nello sblocco');
    } finally {
      setPendingIso(null);
    }
  };

  const [pendingDay, setPendingDay] = useState(null);

  const blockWholeDay = async (day) => {
    if (!window.confirm(`Vuoi bloccare TUTTI gli slot di ${day.label_full}?\n\nGli slot già prenotati dai lead resteranno attivi.`)) return;
    setPendingDay(day.date);
    try {
      const r = await axios.post(`${API}/admin/slot-block-day`, { day: day.date, reason: 'Giornata bloccata da admin' }, { headers });
      toast.success(`${r.data?.blocked ?? 0} slot bloccati su ${day.label_full}`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nel blocco giornata');
    } finally {
      setPendingDay(null);
    }
  };

  const unblockWholeDay = async (day) => {
    setPendingDay(day.date);
    try {
      const r = await axios.delete(`${API}/admin/slot-block-day?day=${day.date}`, { headers });
      toast.success(`${r.data?.deleted ?? 0} slot sbloccati su ${day.label_full}`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore nello sblocco giornata');
    } finally {
      setPendingDay(null);
    }
  };

  // Map slot_iso → booking record (lead or manual_block)
  const bookingByIso = {};
  for (const b of leads) bookingByIso[b.slot_iso] = b;

  const upcomingLeads = leads
    .filter((b) => (b.kind || 'lead') === 'lead' && b.status !== 'cancelled')
    .filter((b) => new Date(b.slot_iso) >= new Date())
    .slice(0, 20);

  return (
    <div className="space-y-6" data-testid="pv-admin-booking-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] font-bold text-pv-tealDeep mb-1">
            <CalendarDays className="w-3.5 h-3.5" /> Gestione disponibilità giovedì
          </div>
          <h2 className="font-heading font-extrabold text-pv-ink text-[20px] md:text-[22px] leading-tight">
            Calendario · prossimi giovedì
          </h2>
          <p className="text-[13px] text-slate-500 mt-1 max-w-2xl">
            Blocca gli slot in cui hai un impegno già fissato (es. su Google Calendar). I lead non li vedranno più disponibili sul sito.
          </p>
        </div>
        <button
          onClick={fetchAll}
          disabled={loading}
          data-testid="pv-admin-booking-refresh"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-pv-teal/40 text-slate-700 text-[12.5px] font-bold transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Aggiorna
        </button>
      </div>

      {/* Calendar grid: 4 Thursdays */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {data.days.map((day) => {
          const futureSlots = day.slots.filter((s) => !s.past);
          const manualBlockedInDay = futureSlots.filter((s) => bookingByIso[s.iso]?.kind === 'manual_block').length;
          const leadsInDay = futureSlots.filter((s) => {
            const b = bookingByIso[s.iso];
            return b && b.kind !== 'manual_block';
          }).length;
          const freeFutureSlots = futureSlots.length - manualBlockedInDay - leadsInDay;
          const allBlocked = manualBlockedInDay > 0 && freeFutureSlots === 0;
          const dayPending = pendingDay === day.date;

          return (
          <div key={day.date} className="bg-white border border-slate-200 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="font-heading font-extrabold text-pv-ink text-[15px] capitalize">{day.label_full}</h3>
              <div className="flex items-center gap-2">
                <span className="text-[10.5px] font-mono uppercase tracking-wider text-slate-500 font-bold">
                  {day.slots.filter((s) => s.available).length} / {day.slots.length} liberi
                </span>
                {allBlocked ? (
                  <button
                    type="button"
                    onClick={() => unblockWholeDay(day)}
                    disabled={dayPending}
                    data-testid={`pv-admin-unblock-day-${day.date}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-300 text-emerald-700 text-[10.5px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50"
                  >
                    {dayPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlock className="w-3 h-3" />}
                    Sblocca tutto
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => blockWholeDay(day)}
                    disabled={dayPending || futureSlots.length === 0}
                    data-testid={`pv-admin-block-day-${day.date}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 hover:bg-red-100 border border-red-300 text-red-700 text-[10.5px] font-bold uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {dayPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />}
                    Blocca tutto
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {day.slots.map((s) => {
                const b = bookingByIso[s.iso];
                const isBlock = b?.kind === 'manual_block';
                const isLead = b?.kind === 'lead' || (b && !b.kind);
                const isPast = s.past;
                const isPending = pendingIso === s.iso;
                let cls, label, action, icon;
                if (isPast) {
                  cls = 'bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed';
                  label = s.label;
                  action = null;
                } else if (isLead) {
                  cls = 'bg-emerald-50 text-emerald-700 border-emerald-300 cursor-default';
                  label = s.label;
                  action = null;
                  icon = <User className="w-3 h-3" />;
                } else if (isBlock) {
                  cls = 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100';
                  label = s.label;
                  action = () => unblockSlot(s.iso);
                  icon = <Lock className="w-3 h-3" />;
                } else {
                  cls = 'bg-white text-slate-700 border-slate-200 hover:border-pv-orange/40 hover:bg-orange-50';
                  label = s.label;
                  action = () => blockSlot(s.iso);
                  icon = <Unlock className="w-3 h-3 opacity-40" />;
                }
                return (
                  <button
                    key={s.iso}
                    type="button"
                    onClick={action}
                    disabled={!action || isPending}
                    data-testid={`pv-admin-slot-${s.iso}`}
                    title={isLead ? `Prenotato da ${b?.first_name || ''} ${b?.last_name || ''}` : isBlock ? 'Click per sbloccare' : isPast ? 'Passato' : 'Click per bloccare'}
                    className={`inline-flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-[12px] font-bold border transition-all relative ${cls}`}
                  >
                    {isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : icon}
                    {label}
                  </button>
                );
              })}
            </div>
            {/* Legend per day */}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-100 text-[10.5px] text-slate-500 font-medium">
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-white border border-slate-300"></span>libero</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>lead</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span>bloccato</span>
            </div>
          </div>
          );
        })}
      </div>

      {/* Upcoming leads list */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pv-admin-upcoming-leads">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-extrabold text-pv-ink text-[15px]">Prossimi appuntamenti dei lead</h3>
          <span className="text-[10px] font-mono uppercase tracking-[0.18em] font-bold bg-pv-mist text-pv-tealDeep px-2 py-1 rounded-full">{upcomingLeads.length} ATTIVI</span>
        </div>
        {upcomingLeads.length === 0 ? (
          <div className="text-center py-8 text-[13px] text-slate-400">Nessun appuntamento futuro dei lead ancora</div>
        ) : (
          <div className="space-y-2">
            {upcomingLeads.map((b) => (
              <div key={b.id} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-slate-50 hover:bg-emerald-50 border border-slate-200 hover:border-emerald-300 transition-colors flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-emerald-100 border border-emerald-300 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-emerald-600" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-bold text-pv-ink text-[14px] truncate">{b.first_name} {b.last_name}</div>
                    <div className="text-[11.5px] text-slate-500 font-mono truncate capitalize">{b.slot_label_full}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {b.phone && (
                    <a href={`tel:${b.phone}`} className="inline-flex items-center gap-1 text-[12px] font-bold text-pv-teal hover:underline">
                      <Phone className="w-3 h-3" />{b.phone}
                    </a>
                  )}
                  {b.email && (
                    <a href={`mailto:${b.email}`} className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-pv-teal">
                      <Mail className="w-3 h-3" />{b.email}
                    </a>
                  )}
                  {b.notes && (
                    <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                      <MessageSquare className="w-3 h-3" />nota
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingPanel;
